import { Box, Link, Typography, Divider, styled, Stack } from "fashion/fabrics";
import BlogHeader from "nodes/blog/BlogHeader";
import CardButton from "nodes/core/CardButton";
import CodexPhysicalPricingGrid from "nodes/codex/CodexPhysicalPricingGrid";
import { POST_INDEX } from "names/blog";

const SectionStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
}));
const BlogPostStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
}));

const CARD_ID_PROFILE = "0271e46e-477d-4d92-a3aa-491af063953f";
const CARD_ID_BUSINESS = "fcb1154b-3db3-4636-aec6-05b0c274fb0b";
const LINK_PROFILE_CARD = `https://memoir.cards/~/${CARD_ID_PROFILE}`;

const BlogPostOrderPhysicalPrints = () => (
  <BlogPostStack>
    <BlogHeader postIndex={POST_INDEX.BLOG_ORDER_PHYSICAL_PRINTS} />

    <SectionStack>
      <Typography>
        We’re excited to announce the new functionality available on Card
        Memoir: <b>Order Physical Card Prints</b>!
      </Typography>

      <Typography component="ul">
        <li>Order publicly sealed card as Physical prints</li>
        <li>Choose between 50 to 1,500 total cards prints</li>
      </Typography>

      <Typography>
        <b>Seamless bridging of physical and digital worlds</b> making all
        interactive calls to action from the card available upon scan of the
        code in the back of the physical cards.
      </Typography>
    </SectionStack>
    <Divider variant="middle" />

    <SectionStack>
      <Box display="flex" justifyContent="center">
        <CardButton cardId={CARD_ID_PROFILE} />
      </Box>
      <Typography variant="h4">Here's how it works:</Typography>
      <Typography>
        Anyone on Card Memoir can request a print order from the application
        while looking at a publicly sealed card.
      </Typography>

      <Typography component="ul">
        <li>
          <b>Choose your Card Model</b>, as example let’s use this{" "}
          <Link href={LINK_PROFILE_CARD}>publicly sealed Profile card</Link>
        </li>
        <li>
          <b>Select “Export As”, “Physical Prints”</b> from top right card menu
          options
        </li>
        <li>
          <b>Prints use white sustainable paper</b>, with brown tint color and
          visible fibers
        </li>
        <li>
          <b>Prints are packaged in decks of 50 cards</b>, with decreasing
          pricing (see bellow)
        </li>
        <li>
          <b>Free shipping worldwide</b>, with total delivery estimated at 1-3
          weeks
        </li>
        <li>
          <b>Confirm the email and get the quote</b>, sending the summary and
          next step
        </li>
      </Typography>

      <Typography>
        The pricing discount is based on the quantity of decks being ordered.
        Please note that you may contact-us directly for larger orders of up to
        500,000 cards.
      </Typography>

      <Typography component="ul">
        <li>
          <b>1 Deck</b>, a total 50 cards for{" "}
          <Typography color="success" fontWeight="500" component="span">
            $50 at $1/card
          </Typography>
        </li>
        <li>
          <b>2 Decks</b>, a total 100 cards for{" "}
          <Typography color="success" fontWeight="500" component="span">
            $90 at $0.9/card{" "}
          </Typography>
          <Typography color="text.secondary" component="span">
            (10% off)
          </Typography>
        </li>
        <li>
          <b>5 Decks</b>, a total 250 cards for{" "}
          <Typography color="success" fontWeight="500" component="span">
            $200 at $0.8/card{" "}
          </Typography>
          <Typography color="text.secondary" component="span">
            (20% off)
          </Typography>
        </li>
        <li>
          <b>10 Decks</b>, a total 500 cards for{" "}
          <Typography color="success" fontWeight="500" component="span">
            $350 at $0.7/card{" "}
          </Typography>
          <Typography color="text.secondary" component="span">
            (30% off)
          </Typography>
        </li>
        <li>
          <b>20 Decks</b>, a total 1,000 cards for{" "}
          <Typography color="success" fontWeight="500" component="span">
            $600 at $0.6/card{" "}
          </Typography>
          <Typography color="text.secondary" component="span">
            (40% off)
          </Typography>
        </li>
        <li>
          <b>30 Decks</b>, a total 1,500 cards for{" "}
          <Typography color="success" fontWeight="500" component="span">
            $750 at $0.5/card{" "}
          </Typography>
          <Typography color="text.secondary" component="span">
            (50% off)
          </Typography>
        </li>
      </Typography>
      <CodexPhysicalPricingGrid />
    </SectionStack>
    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">Quote and next step:</Typography>
      <Typography>
        Upon getting the quote you will receive an email with the summary and
        specification of the desired cards to be ordered.
      </Typography>
      <Typography>
        Please follow the instructions and <b>reply to the email</b> to continue
        the process and you will receive an answer within 1-2 days.
      </Typography>
      <Box display="flex" justifyContent="center">
        <img
          style={{ maxWidth: "min(100%, 476px)" }}
          src={process.env.PUBLIC_URL + "/blog/~e04a_Quote_Email.png"}
          alt="Quote"
        />
      </Box>
    </SectionStack>

    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">Promotional Applications:</Typography>
      <Typography component="ul">
        <li>
          <b>General business promotion</b>: Lead to social media profiles,
          exclusive offers instantly or facilitate app downloads
        </li>
        <li>
          <b>Conference profile cards distribution</b>: Share portfolio summary
          and individuals social media
        </li>
        <li>
          <b>Event or Experience promotion</b>: Link to event registration pages
          and detailed event schedules
        </li>
        <li>
          <b>Physical location promotion</b>: Encourage online reviews directly
          after visit
        </li>
      </Typography>
      <Typography color="text.secondary">
        Example of business promotional card:
      </Typography>
      <Box display="flex" justifyContent="center">
        <CardButton cardId={CARD_ID_BUSINESS} />
      </Box>
    </SectionStack>
  </BlogPostStack>
);

export default BlogPostOrderPhysicalPrints;
